import { createContext, ReactNode, useEffect, useState } from "react";
import { useQuery } from "react-query";

import { $ok } from "foundation-ts/commons";
import { Nullable, UUID } from "foundation-ts/types";
import { UserDto } from "g1-commons/lib/doxecureClientTypes";
import { getUserUuid, isLoggedIn } from "@services/authentication.service";
import { getAdminProfile, getUserProfile } from "@services/users.service";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { data: restoredUser, isLoading: isRestoringSession } = useQuery({
        queryKey: ['restoreSession'],
        queryFn: async () => {
            const savedMode = localStorage.getItem("mode") as AuthMode | null;
            if (isLoggedIn() && $ok(savedMode)) {
                try {
                    const userId = getUserUuid();
                    const user = savedMode === AuthMode.ADMIN
                    ? await getAdminProfile(userId!)
                    : await getUserProfile(userId!);

                    setAuthMode(savedMode);
                    setAuthState(AuthState.AUTHENTICATED);
                    return user;
                } catch (error) {
                    console.error('Restoring session error:', error);
                    localStorage.removeItem("mode");
                    return null;
                }
            }

            return null;
        },
        retry: false,
        staleTime: Infinity, // Ne jamais refetch automatiquement
    });
    const [connectedProfile, setConnectedProfile] = useState<Nullable<UserDto>>(null);
    const [authMode, setAuthMode] = useState<Nullable<AuthMode>>(null);
    const [authState, setAuthState] = useState<AuthState>(AuthState.UNAUTHENTICATED);
    const [pendingAdminId, setPendingAdminId] = useState<Nullable<UUID>>(null);
    const [isInitializing, setIsInitializing] = useState(true);

    const setStandardUser = (user: UserDto) => {
        setConnectedProfile(user);
        setAuthMode($ok(user) ? AuthMode.USER : null);
        setAuthState($ok(user) ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED);
        setPendingAdminId(null);
        localStorage.setItem("mode", AuthMode.USER);
    }

    const setPendingAdmin = (adminId: UUID) => {
        setConnectedProfile(null);
        setAuthMode(null);
        setAuthState(AuthState.PENDING_OTP);
        setPendingAdminId(adminId);
    };

    const setAdminUser = (user: UserDto) => {
        setConnectedProfile(user);
        setAuthMode(AuthMode.ADMIN);
        setAuthState(AuthState.AUTHENTICATED);
        setPendingAdminId(null);
        localStorage.setItem("mode", AuthMode.ADMIN);
    };

    const disconnect = (mode?: AuthMode) => {
        if ($ok(mode) && authMode !== mode) { return; }

        setConnectedProfile(null);
        setAuthMode(null);
        setAuthState(AuthState.UNAUTHENTICATED);
        setPendingAdminId(null);
        localStorage.removeItem("mode");
    }

    const value: AuthContextType = {
        connectedUser: connectedProfile,
        profileId: connectedProfile?.apid,
        pendingAdminId,
        authMode,
        authState,
        isInitializing,
        isAuthenticated: authState === AuthState.AUTHENTICATED,
        isUserAuthenticated: authState === AuthState.AUTHENTICATED && authMode === AuthMode.USER,
        isAdminAuthenticated: authState === AuthState.AUTHENTICATED && authMode === AuthMode.ADMIN,
        isPendingOTP: authState === AuthState.PENDING_OTP,
        setStandardUser,
        setPendingAdmin,
        setAdminUser,
        disconnect,
    }

    useEffect(() => {
        if (!isRestoringSession) {
            if (restoredUser) {
                setConnectedProfile(restoredUser);
            }
            setIsInitializing(false);
        }
    }, [restoredUser, isRestoringSession]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export interface AuthContextType {
    connectedUser: Nullable<UserDto>;
    profileId: Nullable<UUID>;
    pendingAdminId: Nullable<UUID>;
    authMode: Nullable<AuthMode>;
    authState: AuthState;
    isInitializing: boolean;
    isAuthenticated: boolean;
    isUserAuthenticated: boolean;
    isAdminAuthenticated: boolean;
    isPendingOTP: boolean;
    setStandardUser: (user: UserDto) => void;
    setPendingAdmin: (uid: UUID) => void;
    setAdminUser: (user: UserDto) => void;
    disconnect: (mode?: AuthMode) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export enum AuthMode {
    USER = "USER",
    ADMIN = "ADMIN"
}

enum AuthState {
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  PENDING_OTP = 'PENDING_OTP',
  AUTHENTICATED = 'AUTHENTICATED',
}